import {useEffect, useState, useMemo, useRef, useCallback} from 'react';
import SearchService from '../../services/search.services';
import { useCompanyId } from '../../services/CompanyidProvider';
import { FluxButton } from '@meltwater/flux-web-components-react';
import { Stack } from '@mui/material';
import csvDownload from 'json-to-csv-export';
import styles from './search.module.css';
import { useSearchData } from './searchexports';
import DataTable from '../../components/DataTable';


const Filter = ({ load, setActiveTab }) => {
    const [filterData, setFilterData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [severity, setSeverity] = useState('info');
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loadingMessage, setLoadingMessage] = useState('Loading filters...');
    const { filterData: cachedFilterData, setFilterData: setCachedFilterData } = useSearchData();
    const { companyId } = useCompanyId();
    const loadingTimerRef = useRef(null);
    
    // Custom guide steps specific to Filter component
    const filterGuideSteps = useMemo(() => [
        {
            title: 'Filter Management',
            text: 'This guide will help you navigate the Filter Management features.',
            element: null,
        },
        {
            title: 'Search Filters',
            text: 'Use the search box to quickly find filters by name or ID.',
            element: '.mrt-input',
            position: { top: 20, left: 200 }
        },
        {
            title: 'Column Visibility & Table Settings',
            text: 'Use these buttons to show or hide specific columns and adjust table settings.',
            element: '.mrt-button.mrt-dropdown-button',
            position: { top: 0, right: 80 }
        },
        {
            title: 'Export All Data',
            text: 'Export all filters with detailed information about their configuration.',
            element: '.MuiButtonBase-root.FluxButton-root',
            position: { top: 0, left: 100 }
        },
        {
            title: 'Export Selected',
            text: 'Export only the selected filters with their configuration details.',
            element: '.MuiButtonBase-root.FluxButton-root:nth-of-type(2)',
            position: { top: 0, left: 200 }
        },
        {
            title: 'Export Sources',
            text: 'Export the news sources associated with the selected filters.',
            element: '.MuiButtonBase-root.FluxButton-root:nth-of-type(3)',
            position: { top: 0, left: 300 }
        },
        {
            title: 'Export Information',
            text: 'Filter exports include filter configuration details such as countries, languages, news sources, custom categories, and other criteria used for filtering search results.',
            element: null,
        },
        {
            title: 'Refresh Data',
            text: 'Use this button to refresh filter data from the server.',
            element: '.MuiButtonBase-root.FluxButton-root:nth-of-type(4)',
            position: { top: 0, left: 400 }
        },
        {
            title: 'Filter Selection',
            text: 'Select filters with checkboxes to perform operations on specific filters.',
            element: '.mrt-row-select-checkbox',
            position: { top: 100, left: 30 }
        }
    ], []);
    
    const columns = useMemo(
        () => [
            {
                header: 'Name',
                accessorKey: 'name',
            },
            {
                header: 'id',
                accessorKey: 'id',
            },
            {
                header: 'Type',
                accessorFn: (row) => `${row.type}`,
            }
        ],
        [],
    );
    
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setLoadingMessage('Fetching filters...');
        loadingTimerRef.current = setTimeout(() => {
            setLoadingMessage('Please wait, it takes longer than usual for larger companies...');
        }, 15000);
        
        try {
            const myres = await SearchService.getFilters(companyId);
            setFilterData(myres.data);
            setCachedFilterData(myres.data);
            
            setFeedbackMsg(`Successfully loaded ${myres.data.length} filters.`);
            setSeverity('success');
            setOpenSnackBar(true);
            setHasError(false);
        } catch (error) {
            console.log(`FAIL`, error);
            const errorMsg = error.message || 'Unknown error';
            setErrorMessage(errorMsg);
            setFeedbackMsg(`Error loading filters: ${errorMsg}`);
            setSeverity('error');
            setOpenSnackBar(true);
            setHasError(true);
        } finally {
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
            setLoadingMessage('');
            setIsLoading(false);
        }
    }, [companyId, setCachedFilterData, setFeedbackMsg, setSeverity, setOpenSnackBar, setHasError, setErrorMessage]);
    
    useEffect(() => {
        async function loadData() {
            if (cachedFilterData) {
                setFilterData(cachedFilterData);
            } else {
                await fetchData();
            }
        }
        
        if (load) {
            loadData();
        }
        
        return () => {
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
        };
    }, [load, cachedFilterData, companyId, fetchData]);

    const handleCloseSnackBar = () => {
        setOpenSnackBar(false);
    };
    
    const refreshData = async () => {
        try {
            setIsLoading(true);
            setCachedFilterData(null);
            await fetchData();
            setFeedbackMsg('Data refreshed successfully');
            setSeverity('success');
        } catch (err) {
            console.error('Error refreshing data:', err);
            setFeedbackMsg(`Error refreshing data: ${err.message}`);
            setSeverity('error');
        } finally {
            setIsLoading(false);
            setOpenSnackBar(true);
        }
    };
        
    const handleExportData = () => {
        const exportdata = filterData.map((oneitem) => {
            return {
                name: oneitem.name,
                id: oneitem.id,
                visible: oneitem.visible,
                booleans: (oneitem.filters.booleans ?? '').toString(),
                country: (oneitem.filters.country ?? '').toString(),
                sentiment: (oneitem.filters.sentiment ?? '').toString(),
                language: (oneitem.filters.language ?? '').toString(),
                sourceType: (oneitem.filters.sourceType ?? '').toString(),
                subKeyword: (oneitem.filters.subKeyword ?? '').toString(),
                newsSource: (oneitem.filters.newsSource ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                rssSource: (oneitem.filters.rssSource ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                geoname: (oneitem.filters.geoname ?? '').toString(),
                authorList: (oneitem.filters.authorList ?? '').toString(),
                customCategory: (oneitem.filters.customCategory ?? []).map ((subItem) => {
                    return `"${subItem.id}-${subItem.name}"`;
                }),
                newsReach: (oneitem.filters.newsReach ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                newsPremiumTypes: (oneitem.filters.newsPremiumTypes ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                newsOutletTypes: (oneitem.filters.newsOutletTypes ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                broadcastSubtypes: (oneitem.filters.broadcastSubtypes ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                newsMediaFormats: (oneitem.filters.newsMediaFormats ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
            }
        });
        
        const data = {
            data: exportdata,
            delimiter: ',',
            filename: `Filters for ${companyId}`,
        }
        
        csvDownload(data);
    };
    
    const handleExportRows = (rows) => {
        const exportdata = rows.map((oneitem) => {
            return {
                name: oneitem.original.name,
                id: oneitem.original.id,
                visible: oneitem.original.visible,
                booleans: (oneitem.original.filters.booleans ?? '').toString(),
                country: (oneitem.original.filters.country ?? '').toString(),
                sentiment: (oneitem.original.filters.sentiment ?? '').toString(),
                language: (oneitem.original.filters.language ?? '').toString(),
                sourceType: (oneitem.original.filters.sourceType ?? '').toString(),
                subKeyword: (oneitem.original.filters.subKeyword ?? '').toString(),
                newsSource: (oneitem.original.filters.newsSource ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                rssSource: (oneitem.original.filters.rssSource ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                geoname: (oneitem.original.filters.geoname ?? '').toString(),
                authorList: (oneitem.original.filters.authorList ?? '').toString(),
                customCategory: (oneitem.original.filters.customCategory ?? []).map ((subItem) => {
                    return `"${subItem.id}-${subItem.name}"`;
                }),
                newsReach: (oneitem.original.filters.newsReach ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                newsPremiumTypes: (oneitem.original.filters.newsPremiumTypes ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                newsOutletTypes: (oneitem.original.filters.newsOutletTypes ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                broadcastSubtypes: (oneitem.original.filters.broadcastSubtypes ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
                newsMediaFormats: (oneitem.original.filters.newsMediaFormats ?? []).map ((subItem) => {
                    return `"${subItem.id}"`;
                }),
            }
        });
        
        const data = {
            data: exportdata,
            delimiter: ',',
            filename: `Filters for ${companyId}`,
        }
        
        csvDownload(data);
    };

    const handleExportSources = async (rows) => {
        let promises = rows.map(row => SearchService.getFilterById(companyId, row.original.id));

        Promise.all(promises).then((responses) => {
            let filters = responses.map(response => response.data);
            let sourceRows = []
            filters.forEach((filter) => {
                (filter.filters?.newsSource ?? []).forEach((source) => {
                    sourceRows.push({filterName: filter.name, ...source});
                });
            });
            const data = {
                data: sourceRows,
                delimiter: ',',
                filename: `Filter Sources for ${companyId}`,
            }
            csvDownload(data)
        });
    }
    
    // Custom toolbar actions for the DataTable
    const renderToolbarActions = ({ table }) => {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <FluxButton
                    style={{ margin: 2 }}
                    tint="primary"
                    type="filled"
                    onClick={handleExportData}
                >
                    Export All Data
                </FluxButton>
                <FluxButton
                    style={{ margin: 2 }}
                    tint="accent"
                    type="filled"
                    onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
                    disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                >
                    Export Selected
                </FluxButton>
                <FluxButton
                    style={{ margin: 2 }}
                    tint="accent"
                    type="filled"
                    onClick={() => handleExportSources(table.getSelectedRowModel().rows)}
                    disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                >
                    Export Sources
                </FluxButton>
                <FluxButton
                    style={{ margin: 2 }}
                    tint="secondary"
                    type="filled"
                    onClick={refreshData}
                >
                    Refresh Data
                </FluxButton>
            </Stack>
        );
    };
    
    return (
        <div className={styles.Search}>
            <DataTable 
                columns={columns}
                data={filterData || []}
                isLoading={isLoading && !hasError}
                loadingMessage={loadingMessage || 'Loading filters...'}
                hasError={hasError}
                errorMessage={errorMessage}
                errorTitle="Error Loading Filters"
                onBackClick={() => setActiveTab(null)}
                renderToolbarActions={renderToolbarActions}
                backTooltip="Return to Dashboard"
                showGuide={false}
                guideSteps={filterGuideSteps}
                snackbarProps={{
                    open: openSnackBar,
                    onClose: handleCloseSnackBar,
                    message: feedbackMsg,
                    severity: severity,
                    autoHideDuration: 10000
                }}
                tableProps={{
                    muiSearchTextFieldProps: {
                        placeholder: 'Search filters...',
                        variant: 'standard',
                        sx: { width: '300px' }
                    },
                    initialState: {
                        density: 'compact',
                        pagination: { pageSize: 100 },
                    }
                }}
            />
        </div>
    );
};

export default Filter;
